import { ReactNode, useEffect, useMemo } from "react";
import * as Highcharts from "highcharts/highstock";

import type { BrandingConfig } from "@utilifeed/config/branding";
import { DEFAULT_BRANDING } from "@utilifeed/config/branding";

import { BrandingContext, type AppIdentifier } from "../contexts/BrandingContext";
import { EnergyUnitPrefixContext } from "../contexts/EnergyUnitPrefixContext";
import { createThemeFromBranding } from "./theme.factory";

export type BrandingProviderProps = {
  children: ReactNode;
  /** Organization branding from the API. Falls back to DEFAULT_BRANDING when null/undefined. */
  branding?: BrandingConfig | null;
  /** Optional resolver that maps branding fontFamily to the actual CSS font-family name.
   *  Injected by the app to support font registries without creating a lib - app dependency. */
  fontFamilyResolver?: (fontFamily: string) => string;
  /** Which app is mounting the provider. Reachable via useAppIdentifier(). */
  app?: AppIdentifier;
};

function useBrandingSideEffects(branding: BrandingConfig) {
  useEffect(() => {
    const { graphColors } = branding;
    Highcharts.setOptions({
      colors: Object.values(graphColors),
    });

    if (branding.text?.appTitle) {
      document.title = branding.text.appTitle;
    }

    if (branding.logos?.favicon) {
      const favicon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (favicon) favicon.href = branding.logos.favicon;
    }
  }, [branding]);
}

/** Merge API branding with defaults so empty/missing fields get filled in. */
// eslint-disable-next-line sonarjs/cognitive-complexity
function mergeWithDefaults(partial: BrandingConfig | null | undefined): BrandingConfig {
  if (!partial) return DEFAULT_BRANDING;

  return {
    colors: {
      primary: partial.colors?.primary || DEFAULT_BRANDING.colors.primary,
      secondary: partial.colors?.secondary || DEFAULT_BRANDING.colors.secondary,
      tertiary: partial.colors?.tertiary || DEFAULT_BRANDING.colors.tertiary,
      primaryDark: partial.colors?.primaryDark || DEFAULT_BRANDING.colors.primaryDark,
      primaryLight: partial.colors?.primaryLight || DEFAULT_BRANDING.colors.primaryLight,
      surface: partial.colors?.surface || DEFAULT_BRANDING.colors.surface,
      container: partial.colors?.container || DEFAULT_BRANDING.colors.container,
      fontPrimary: partial.colors?.fontPrimary || DEFAULT_BRANDING.colors.fontPrimary,
      fontSecondary: partial.colors?.fontSecondary || DEFAULT_BRANDING.colors.fontSecondary,
      fontInverted: partial.colors?.fontInverted || DEFAULT_BRANDING.colors.fontInverted,
      card: partial.colors?.card || DEFAULT_BRANDING.colors.card,
      divider1: partial.colors?.divider1 || DEFAULT_BRANDING.colors.divider1,
      divider2: partial.colors?.divider2 || DEFAULT_BRANDING.colors.divider2,
      inverseSurface: partial.colors?.inverseSurface || DEFAULT_BRANDING.colors.inverseSurface,
      outline: partial.colors?.outline || DEFAULT_BRANDING.colors.outline,
    },
    logos: {
      light: partial.logos?.light || DEFAULT_BRANDING.logos.light,
      dark: partial.logos?.dark || DEFAULT_BRANDING.logos.dark,
      favicon: partial.logos?.favicon || DEFAULT_BRANDING.logos.favicon,
    },
    typography: {
      fontFamily: partial.typography?.fontFamily || DEFAULT_BRANDING.typography.fontFamily,
    },
    text: {
      appTitle: partial.text?.appTitle || DEFAULT_BRANDING.text.appTitle,
      seoDescription: partial.text?.seoDescription || DEFAULT_BRANDING.text.seoDescription,
      companyName: partial.text?.companyName || DEFAULT_BRANDING.text.companyName,
    },
    graphColors: {
      graph1: partial.graphColors?.graph1 || DEFAULT_BRANDING.graphColors.graph1,
      graph2: partial.graphColors?.graph2 || DEFAULT_BRANDING.graphColors.graph2,
      graph3: partial.graphColors?.graph3 || DEFAULT_BRANDING.graphColors.graph3,
      graph4: partial.graphColors?.graph4 || DEFAULT_BRANDING.graphColors.graph4,
      graph5: partial.graphColors?.graph5 || DEFAULT_BRANDING.graphColors.graph5,
      graph6: partial.graphColors?.graph6 || DEFAULT_BRANDING.graphColors.graph6,
      graph7: partial.graphColors?.graph7 || DEFAULT_BRANDING.graphColors.graph7,
      graph8: partial.graphColors?.graph8 || DEFAULT_BRANDING.graphColors.graph8,
      graph9: partial.graphColors?.graph9 || DEFAULT_BRANDING.graphColors.graph9,
      graph10: partial.graphColors?.graph10 || DEFAULT_BRANDING.graphColors.graph10,
    },
    urls: {
      website: partial.urls?.website || DEFAULT_BRANDING.urls.website,
    },
    preferences: {
      PropAIenergyprefix:
        partial.preferences?.PropAIenergyprefix ?? DEFAULT_BRANDING.preferences.PropAIenergyprefix,
    },
  };
}

export function BrandingProvider({
  children,
  branding: brandingProp,
  fontFamilyResolver,
  app = "propai",
}: BrandingProviderProps) {
  const branding = useMemo(() => mergeWithDefaults(brandingProp), [brandingProp]);

  const resolvedFontFamily = useMemo(
    () => fontFamilyResolver?.(branding.typography.fontFamily),
    [branding.typography.fontFamily, fontFamilyResolver]
  );

  const theme = useMemo(
    () => createThemeFromBranding(branding, resolvedFontFamily),
    [branding, resolvedFontFamily]
  );

  useBrandingSideEffects(branding);

  const contextValue = useMemo(
    () => ({
      branding,
      theme,
      app,
      isLoading: false,
      error: null,
    }),
    [branding, theme, app]
  );

  return (
    <BrandingContext.Provider value={contextValue}>
      <EnergyUnitPrefixContext.Provider value={branding.preferences.PropAIenergyprefix}>
        {children}
      </EnergyUnitPrefixContext.Provider>
    </BrandingContext.Provider>
  );
}
